import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ThemeToggle } from "@/components/ThemeToggle";

const links = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "expertise", label: "Expertise" },
  { id: "experience", label: "Experience" },
  { id: "achievements", label: "Achievements" },
  { id: "blogs", label: "Blogs" },
  { id: "testimonials", label: "Testimonials" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      if (!isHome) return;
      const sections = links.map((l) => document.getElementById(l.id));
      const y = window.scrollY + 140;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.offsetTop <= y) { setActive(links[i].id); break; }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const handleNav = (id) => {
    setOpen(false);
    if (!isHome) {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="container-x">
        <div className={`flex items-center justify-between rounded-full px-5 md:px-7 py-3 transition-all duration-500 ${scrolled ? "glass-strong" : "glass"}`}>
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(var(--gold-light))] to-[hsl(var(--gold-deep))] flex items-center justify-center shadow-lg">
              <span className="font-serif text-[hsl(var(--navy-deep))] font-bold text-sm">SR</span>
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="font-serif text-base font-semibold tracking-wide">Shivaji Rajan</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Business Leader</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => handleNav(l.id)}
                className={`relative px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                  active === l.id && isHome ? "text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" : "text-foreground/80 hover:text-foreground"
                }`}
              >
                {l.label}
                {active === l.id && isHome && (
                  <motion.span layoutId="nav-active" className="absolute inset-0 rounded-full bg-[hsl(var(--gold))]/12 -z-10" transition={{ type: "spring", stiffness: 380, damping: 30 }} />
                )}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden h-10 w-10 rounded-full glass flex items-center justify-center"
              aria-label="Menu"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="lg:hidden mt-3 glass-strong rounded-2xl overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="p-3 flex flex-col">
                {links.map((l) => (
                  <button
                    key={l.id}
                    data-testid={`mobile-nav-${l.id}`}
                    onClick={() => handleNav(l.id)}
                    className="text-left px-4 py-3 rounded-xl hover:bg-[hsl(var(--gold))]/10 transition-colors font-medium"
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
